export interface BestMove {
  /** First UCI move of the principal variation, e.g. "e2e4". */
  readonly uci: string;
  /** Centipawn score from the side-to-move perspective (mate → ±1e6). */
  readonly scoreCp: number;
  /** Full principal variation as UCI moves. */
  readonly pv: string[];
}

const PV_RE = /^(.*?)\bmultipv (\d+)\b(.*?)\bscore (cp|mate) (-?\d+)\b(.*?)\bpv (.+)$/;

/** Parse all MultiPV `info` lines into ordered best moves. */
export function parseBestMoves(lines: readonly string[]): BestMove[] {
  const byIndex = new Map<number, BestMove>();
  for (const line of lines) {
    const m = line.match(PV_RE);
    if (!m) continue;
    const idx = Number(m[2]);
    const kind = m[4];
    const raw = Number(m[5]);
    const scoreCp = kind === 'mate' ? (raw > 0 ? 1_000_000 : -1_000_000) : raw;
    const pv = (m[7] ?? '').trim().split(/\s+/).filter(Boolean);
    const uci = pv[0] ?? '';
    byIndex.set(idx, { uci, scoreCp, pv });
  }
  return [...byIndex.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([, v]) => v)
    .filter((b) => b.uci.length >= 4);
}

/** Extract the PV from a single `info ... pv ...` line. */
export function parsePvLine(line: string): string[] {
  const idx = line.indexOf(' pv ');
  if (idx === -1) return [];
  return line
    .slice(idx + 4)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

/** Extract a single `score cp N` / `score mate N` value. */
export function parseScoreLine(line: string): number {
  const m = line.match(/\bscore (cp|mate) (-?\d+)\b/);
  if (!m) return 0;
  const kind = m[1];
  const raw = Number(m[2]);
  if (kind === 'mate') return raw > 0 ? 1_000_000 : -1_000_000;
  return raw;
}
